import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { SubsidiarySelectionTable } from '@features/downloads/partials/SubsidiarySelectionTable';
import { Button, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { getManageGroupsUrl, hasInvalidCharacters, isNameDuplicate, getInvalidNameMessage } from './utils';
import {
  useGetSubsidiaryGroupsQuery,
  useCreateSubsidiaryGroupMutation,
  useUpdateSubsidiaryGroupMutation,
} from '@api/subsidiary-groups';
import type { InitiativeGroup } from '../../../types/initiative-group';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';

interface Props {
  initiativeId: string;
  group?: InitiativeGroup;
}

interface FormState {
  name: string;
  description: string;
  selectedSubsidiaryIds: string[];
}

const LABEL_STYLE = { fontSize: '13px', fontWeight: 500, lineHeight: '16px' } as const;

export const SubsidiaryGroupForm = ({ initiativeId, group }: Props) => {
  const history = useHistory();
  const isEditMode = Boolean(group?._id);

  const [form, setForm] = useState<FormState>(() => getInitialForm(group));
  const [nameTouched, setNameTouched] = useState(false);

  const { data: apiGroups, error: groupsError } = useGetSubsidiaryGroupsQuery(initiativeId);
  const [createGroup, { isLoading: isCreating }] = useCreateSubsidiaryGroupMutation();
  const [updateGroup, { isLoading: isUpdating }] = useUpdateSubsidiaryGroupMutation();

  useEffect(() => {
    if (groupsError) {
      toastFromError({ error: groupsError, title: 'Failed to load groups' });
    }
  }, [groupsError]);

  const existingGroups = apiGroups || [];
  const isSaving = isCreating || isUpdating;

  const handleFormChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    history.push(getManageGroupsUrl(initiativeId));
  };

  const handleSubmit = async () => {
    try {
      const baseData = {
        name: form.name,
        description: form.description || undefined,
        group: form.selectedSubsidiaryIds.map((id) => ({
          initiativeId: id,
        })),
      };

      if (isEditMode && group?._id) {
        await updateGroup({
          initiativeId,
          groupId: group._id,
          data: { ...baseData, _id: group._id },
        }).unwrap();
        generateToast({
          color: 'success',
          title: 'Success',
          message: 'Subsidiary group updated successfully',
        });
      } else {
        await createGroup({
          initiativeId,
          data: baseData,
        }).unwrap();
        generateToast({
          color: 'success',
          title: 'Success',
          message: 'Subsidiary group created successfully',
        });
      }

      history.push(getManageGroupsUrl(initiativeId));
    } catch (error) {
      toastFromError({ error, title: isEditMode ? 'Failed to update group' : 'Failed to create group' });
    }
  };

  const isDuplicate = isNameDuplicate(form.name, existingGroups, group?._id);
  const hasInvalidChars = hasInvalidCharacters(form.name);
  const invalidNameMessage = nameTouched ? getInvalidNameMessage(form.name, isDuplicate, hasInvalidChars) : null;
  const hasValidName = form.name.length > 0 && !isDuplicate && !hasInvalidChars;
  const hasSelectedSubsidiaries = form.selectedSubsidiaryIds.length > 0;
  const isFormValid = hasValidName && hasSelectedSubsidiaries && !isSaving;

  return (
    <>
      {/* Subsidiary group name */}
      <FormGroup>
        <Label for='groupName' className='text-ThemeTextDark' style={LABEL_STYLE}>
          Subsidiary group name
        </Label>
        <Input
          id='groupName'
          type='text'
          placeholder='Enter subsidiary group name'
          value={form.name}
          onChange={(e) => handleFormChange('name', e.target.value)}
          onBlur={() => setNameTouched(true)}
          invalid={!!invalidNameMessage}
          disabled={isSaving}
        />
        {invalidNameMessage && <FormFeedback className='d-block'>{invalidNameMessage}</FormFeedback>}
      </FormGroup>

      {/* Description */}
      <FormGroup>
        <Label for='groupDescription' className='text-ThemeTextDark' style={LABEL_STYLE}>
          Description
        </Label>
        <Input
          id='groupDescription'
          type='textarea'
          placeholder='Description of group (optional)'
          value={form.description}
          onChange={(e) => handleFormChange('description', e.target.value)}
          rows={3}
          disabled={isSaving}
        />
      </FormGroup>

      {/* Subsidiary selection */}
      <div className='mt-4'>
        <Label className='text-ThemeTextDark' style={LABEL_STYLE}>
          Select subsidiaries for group
        </Label>
        <SubsidiarySelectionTable
          initiativeId={initiativeId}
          selectedIds={form.selectedSubsidiaryIds}
          handleSetSelectedIds={(ids) => handleFormChange('selectedSubsidiaryIds', ids)}
        />
      </div>

      {/* Buttons */}
      <div className='d-flex justify-content-end gap-3 mt-5 pt-3'>
        <Button color='link-secondary' onClick={handleCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          color='primary'
          disabled={!isFormValid}
          onClick={handleSubmit}
          className='d-flex align-items-center gap-2'
        >
          <i className={`fa-light ${isSaving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} />
          {isSaving ? 'Saving...' : isEditMode ? 'Save changes' : 'Create subsidiary group'}
        </Button>
      </div>
    </>
  );
};

function getInitialForm(group?: InitiativeGroup): FormState {
  return {
    name: group?.name || '',
    description: group?.description || '',
    selectedSubsidiaryIds: group?.group?.map((g) => g.initiativeId) || [],
  };
}

